import React from "react";
import { Grid } from "@material-ui/core";

import {
  ControlledLifeCycleInput,
  DollarDisplayCentsInputProps,
  withNamespace,
} from "components/Form/FormikControls";

import Big from "big.js";
import { CommonPaymentFields } from "./CommonPaymentMethodFields";

export function cashRoundCents(
  amountCents: Big,
  smallestDenominationCents: number = 5,
): Big {
  try {
    return amountCents
      .div(smallestDenominationCents <= 0 ? 1 : smallestDenominationCents)
      .round(0, 2) // Round half up
      .times(smallestDenominationCents);
  } catch (e) {
    return new Big(0);
  }
}

export function calculateCashChangeCents(
  paymentAmountCents: number,
  tenderedAmountCents: number,
  smallestDenominationCents: number = 5,
): Big | null {
  const paymentValueCents = cashRoundCents(new Big(paymentAmountCents));
  const tenderedValueCents = cashRoundCents(new Big(tenderedAmountCents));

  const isChangeCalculable =
    paymentValueCents.gte(0) && tenderedValueCents.gt(0);

  if (!isChangeCalculable) {
    return null;
  }

  try {
    const changeCents = cashRoundCents(
      tenderedValueCents.minus(paymentValueCents),
      smallestDenominationCents,
    );
    return changeCents.gte(0) ? changeCents : null;
  } catch (e) {
    return null;
  }
}

const SuggestedChangeInputProps = {
  ...DollarDisplayCentsInputProps,
  formatFieldValue: (tenderedCents: number, props) => {
    const {
      multiplier, // specified in DollarDisplayCentsInputProps
      paymentAmountCents, // provided via CashPaymentMethodForm
    } = props;

    const change = calculateCashChangeCents(
      paymentAmountCents || 0, // may be NaN if the user has entered invalid input
      tenderedCents || 0, // may be NaN if the user has entered invalid input
    );
    if (change !== null) {
      return change.times(multiplier);
    }
    return null;
  },
  type: "text",
  disabled: true,
};

interface CashPaymentMethodFormProps {
  namespace?: string;
  paymentAmountCents: number;
}

export function CashPaymentMethodForm(
  props: CashPaymentMethodFormProps,
): React.JSX.Element {
  const { namespace: ns = "", paymentAmountCents } = props;

  return (
    <>
      <Grid item sm={6}>
        <ControlledLifeCycleInput
          {...DollarDisplayCentsInputProps}
          label="Tendered cash ($)"
          name={withNamespace(ns, "amountTenderedCents")}
        />
      </Grid>
      <Grid item sm={6}>
        <ControlledLifeCycleInput
          {...SuggestedChangeInputProps}
          key={paymentAmountCents || 0} // force re-mount when payment amount changes
          label="Suggested change ($)"
          name={withNamespace(ns, "amountTenderedCents")}
          paymentAmountCents={paymentAmountCents}
        />
      </Grid>
      <CommonPaymentFields namespace={ns} />
    </>
  );
}
